// src/pages/BatchImport.jsx
//
// Import batch d'un ZIP complet structuré :
//   EXPERTISE.zip
//   ├── ARTISTE_A/
//   │   ├── CV_artiste.pdf          ← rattaché à toutes les fiches de cet artiste
//   │   ├── AA-1/
//   │   │   ├── AA-1.pdf
//   │   │   ├── Constat.pdf
//   │   │   └── photo.jpg
//   │   └── AA-2/
//   │       └── ...
//   └── ARTISTE_B/
//       └── XF-166/
//           └── ...
//
// Branchement :
//   1. Ajouter la route dans App.js :
//      <Route path="/batch-import" element={<BatchImport />} />
//   2. Ajouter un bouton dans la page Ma Collection :
//      <Link to="/batch-import">Importer un dossier ZIP</Link>

import React, { useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, CheckCircle, AlertCircle, Loader2,
  FolderOpen, FileText, Image as ImageIcon, ChevronDown,
  ChevronUp, X,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DOC_RULES = [
  { pattern: /certif|authenticit/i,              type: "certificate",     label: "Certificat d'authenticité" },
  { pattern: /facture|invoice|achat|vente/i,     type: "invoice",         label: "Facture" },
  { pattern: /constat|conservation|condition/i,  type: "condition_report",label: "Rapport de conservation" },
  { pattern: /exposition|exhibit|catalog/i,      type: "exhibition",      label: "Document d'exposition" },
  { pattern: /donation|don\b|legs|testament/i,   type: "other",           label: "Donation / legs" },
  { pattern: /bio|cv\b|curriculum|artiste/i,     type: "other",           label: "Biographie artiste" },
  { pattern: /^[a-z]{1,5}[-_]\d+(\.\d+)?\.pdf$/i, type: "certificate",   label: "Fiche œuvre" },
];

function guessDocType(filename) {
  for (const r of DOC_RULES) {
    if (r.pattern.test(filename)) return { type: r.type, label: r.label };
  }
  return { type: "other", label: "Autre" };
}

const isImage = (f) => /\.(jpe?g|png|webp|gif)$/i.test(f);
const isPdf   = (f) => /\.pdf$/i.test(f);

// Analyse la structure du ZIP → retourne { artists: [{name, cvFiles, artworks: [{folder, images, docs}]}] }
async function parseZipStructure(zipFile) {
  const JSZip = (await import("jszip")).default;
  const zip   = await JSZip.loadAsync(zipFile);

  // Collecter tous les chemins de fichiers
  const allPaths = [];
  zip.forEach((relativePath, entry) => {
    if (!entry.dir) allPaths.push(relativePath);
  });

  // Détecter le préfixe racine à ignorer
  // Si tous les fichiers commencent par le même dossier (ex: "Dossier EXPERTISE/...")
  // on le saute automatiquement
  const firstParts = allPaths.map(p => p.split("/").filter(Boolean)[0]);
  const uniqueRoots = [...new Set(firstParts)];
  const skipRoot = uniqueRoots.length === 1 ? uniqueRoots[0] : null;

  const tree = {};

  zip.forEach((relativePath, entry) => {
    if (entry.dir) return;
    let parts = relativePath.split("/").filter(Boolean);

    // Ignorer le dossier racine unique s'il existe
    if (skipRoot && parts[0] === skipRoot) parts = parts.slice(1);

    if (parts.length < 2) return;

    const filename = parts[parts.length - 1];
    if (filename.startsWith(".")) return;

    const artistName = parts[0];
    if (!tree[artistName]) tree[artistName] = { cvFiles: [], artworks: {} };

    if (parts.length === 2) {
      // Fichier à la racine de l'artiste → CV
      if (isPdf(filename)) {
        tree[artistName].cvFiles.push({ filename, entry });
      }
    } else if (parts.length === 3) {
      // Fichier dans le sous-dossier œuvre
      const inventoryNumber = parts[1];
      if (!tree[artistName].artworks[inventoryNumber]) {
        tree[artistName].artworks[inventoryNumber] = { images: [], docs: [] };
      }
      if (isImage(filename)) {
        tree[artistName].artworks[inventoryNumber].images.push({ filename, entry });
      } else if (isPdf(filename)) {
        const { type, label } = guessDocType(filename);
        tree[artistName].artworks[inventoryNumber].docs.push({ filename, entry, type, label });
      }
    }
    // Niveaux plus profonds → ignorés
  });

  const artists = Object.entries(tree).map(([name, data]) => ({
    name,
    cvFiles: data.cvFiles,
    artworks: Object.entries(data.artworks).map(([inv, files]) => ({
      inventoryNumber: inv,
      images: files.images,
      docs:   files.docs,
    })),
  })).filter(a => a.artworks.length > 0);

  return artists;
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function BatchImport() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep]               = useState("idle"); // idle|parsing|preview|importing|done|error
  const [artists, setArtists]         = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [expandedArtist, setExpandedArtist]         = useState(null);
  const [enrichMode,     setEnrichMode]             = useState(false);
  const [progress, setProgress]       = useState({ current: 0, total: 0, label: "", errors: [] });
  const [errorMsg, setErrorMsg]       = useState("");

  // Charger les collections dès le montage
  React.useEffect(() => {
    if (!user?.id) return;
    console.log("[BatchImport] chargement collections pour user:", user.id);
    supabase
      .from("collections")
      .select("id, title")
      .eq("client_id", user.id)
      .then(({ data, error }) => {
        console.log("[BatchImport] collections result:", { data, error });
        if (error) {
          console.error("[BatchImport] erreur collections:", error);
          setErrorMsg("Impossible de charger vos collections : " + error.message);
          setStep("error");
          return;
        }
        setCollections(data || []);
        if (data?.length === 1) setSelectedCollection(data[0].id);
      });
  }, [user?.id]);

  // ── Sélection ZIP ──────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setStep("parsing");
    setErrorMsg("");

    try {
      const parsed = await parseZipStructure(file);
      if (parsed.length === 0) {
        setErrorMsg("Aucune œuvre détectée. Vérifiez la structure : ZIP > Artiste > N°Inventaire > fichiers");
        setStep("error");
        return;
      }
      setArtists(parsed);
      setStep("preview");
    } catch (err) {
      console.error("[BatchImport]", err);
      setErrorMsg("Impossible de lire ce ZIP : " + (err.message || err));
      setStep("error");
    }
  };

  // ── Upload un fichier vers Storage ────────────────────────────────────────
  const uploadFile = useCallback(async (entry, artworkId, bucket, contentType) => {
    const blob = await entry.async("blob");
    const ext  = entry.name.split(".").pop();
    const path = `${artworkId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, blob, { upsert: true, contentType });
    if (error) throw error;
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
    return pub.publicUrl;
  }, []);

  // ── Import effectif ───────────────────────────────────────────────────────
  const handleConfirmImport = async () => {
    if (!selectedCollection || !user?.id) return;

    // Compter le total de fichiers
    const totalArtworks = artists.reduce((n, a) => n + a.artworks.length, 0);
    setStep("importing");
    setProgress({ current: 0, total: totalArtworks, label: "Démarrage…", errors: [] });

    let doneCount = 0;
    const errors  = [];

    for (const artist of artists) {
      // 1. Upload CVs de l'artiste une seule fois → URLs réutilisées
      const cvDocs = [];
      for (const cv of artist.cvFiles) {
        try {
          // CV uploadé dans un dossier générique "artist-cvs"
          const blob = await cv.entry.async("blob");
          const path = `${artist.name.replace(/\s+/g, "_")}/${Date.now()}-${cv.filename}`;
          const { error: upErr } = await supabase.storage
            .from("artworks-documents")
            .upload(path, blob, { upsert: true, contentType: "application/pdf" });
          if (!upErr) {
            const { data: pub } = supabase.storage.from("artworks-documents").getPublicUrl(path);
            cvDocs.push({
              id:    `doc_${Date.now()}_cv`,
              name:  cv.filename.replace(/\.pdf$/i, ""),
              type:  "other",
              label: "Biographie artiste",
              file:  pub.publicUrl,
              date:  new Date().toISOString(),
            });
          }
        } catch (e) {
          console.warn("[BatchImport] CV upload failed:", cv.filename, e);
        }
      }

      // 2. Pour chaque œuvre de cet artiste
      for (const artwork of artist.artworks) {
        doneCount += 1;
        const currentDoneCount = doneCount;
        setProgress((p) => ({
          ...p,
          current: currentDoneCount,
          label: `${artist.name} / ${artwork.inventoryNumber}`,
        }));

        try {
          // 2a. Chercher fiche existante par inventory_number (mode enrichissement)
          let artworkId = null;

          if (enrichMode) {
            const { data: existing } = await applyProposalFilter(supabase
              .from("artworks")
              .select("id")
              .eq("inventory_number", artwork.inventoryNumber)
              .eq("client_id", user.id), await proposalCollectionIds(user.id))
              .maybeSingle();

            if (existing?.id) {
              artworkId = existing.id;
            }
          }

          // Si pas trouvé (ou mode création) → INSERT
          if (!artworkId) {
            const { data: newArtwork, error: insertErr } = await supabase
              .from("artworks")
              .insert({
                title:            artwork.inventoryNumber,
                artist:           artist.name,
                inventory_number: artwork.inventoryNumber,
                collection_id:    selectedCollection,
                client_id:        user.id,
              })
              .select("id")
              .single();

            if (insertErr) throw insertErr;
            artworkId = newArtwork.id;
          }

          // 2b. Upload images
          const uploadedImages = [];
          for (const img of artwork.images) {
            try {
              const url = await uploadFile(img.entry, artworkId, "artworks-images", "image/jpeg");
              uploadedImages.push({
                id:        `img_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                url, path: url, publicUrl: url,
                isPrimary: uploadedImages.length === 0,
                filename:  img.filename,
              });
            } catch (e) { console.warn("[BatchImport] img:", img.filename, e); }
          }

          // 2c. Upload documents de l'œuvre
          const uploadedDocs = [...cvDocs]; // CV artiste inclus
          for (const doc of artwork.docs) {
            try {
              const url = await uploadFile(doc.entry, artworkId, "artworks-documents", "application/pdf");
              uploadedDocs.push({
                id:   `doc_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                name: doc.filename.replace(/\.pdf$/i, ""),
                type: doc.type,
                label: doc.label,
                file: url,
                date: new Date().toISOString(),
              });
            } catch (e) { console.warn("[BatchImport] doc:", doc.filename, e); }
          }

          // 2d. Mettre à jour la fiche avec images + docs
          const updatePayload = {};
          if (uploadedImages.length > 0) {
            updatePayload.images    = uploadedImages;
            updatePayload.image_url = uploadedImages[0].url;
          }
          if (uploadedDocs.length > 0) {
            updatePayload.documents = JSON.stringify(uploadedDocs);
          }

          if (Object.keys(updatePayload).length > 0) {
            await supabase.from("artworks").update(updatePayload).eq("id", artworkId);
          }

        } catch (err) {
          console.error("[BatchImport] Fiche échouée:", artist.name, artwork.inventoryNumber, err);
          errors.push(`${artist.name} / ${artwork.inventoryNumber} : ${err.message || err}`);
        }

        setProgress(p => ({ ...p, errors }));
      }
    }

    setProgress(p => ({ ...p, label: "Terminé", errors }));
    setStep("done");
  };

  const handleReset = () => {
    setStep("idle");
    setArtists([]);
    setErrorMsg("");
    setProgress({ current: 0, total: 0, label: "", errors: [] });
  };

  // Totaux pour affichage
  const totalArtworks = artists.reduce((n, a) => n + a.artworks.length, 0);
  const totalFiles    = artists.reduce((n, a) =>
    n + a.cvFiles.length + a.artworks.reduce((m, w) => m + w.images.length + w.docs.length, 0), 0);

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-gray-500 hover:text-black mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Ma Collection
          </Link>
          <h1 className="text-3xl font-serif mb-1">Import batch</h1>
          <p className="text-gray-500 text-sm">
            Importez un dossier ZIP structuré pour créer plusieurs fiches en une seule opération.
          </p>
        </div>

        {/* Structure attendue */}
        {step === "idle" && (
          <div className="mb-8 p-5 rounded-xl border border-gray-200 bg-gray-50">
            <p className="text-xs font-mono text-gray-500 leading-relaxed">
              EXPERTISE.zip<br />
              ├── ARTISTE_NOM/<br />
              │&nbsp;&nbsp;&nbsp;├── Bio_artiste.pdf&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;← CV rattaché à toutes ses fiches<br />
              │&nbsp;&nbsp;&nbsp;├── AA-1/<br />
              │&nbsp;&nbsp;&nbsp;│&nbsp;&nbsp;&nbsp;├── AA-1.pdf<br />
              │&nbsp;&nbsp;&nbsp;│&nbsp;&nbsp;&nbsp;├── Constat.pdf<br />
              │&nbsp;&nbsp;&nbsp;│&nbsp;&nbsp;&nbsp;└── photo.jpg<br />
              │&nbsp;&nbsp;&nbsp;└── AA-2/<br />
              └── AUTRE_ARTISTE/<br />
              &nbsp;&nbsp;&nbsp;&nbsp;└── XF-166/<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└── ...
            </p>
          </div>
        )}

        {/* ── IDLE ── */}
        {step === "idle" && (
          <div className="space-y-6">
            {/* Sélection collection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Collection de destination
              </label>
              <select
                value={selectedCollection}
                onChange={e => setSelectedCollection(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              >
                <option value="">Sélectionner une collection…</option>
                {collections.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>

            {/* Zone upload */}
            <div
              onClick={() => selectedCollection && fileInputRef.current?.click()}
              className={[
                "border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center transition cursor-pointer",
                selectedCollection
                  ? "border-gray-300 hover:border-black"
                  : "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60",
              ].join(" ")}
            >
              <Upload className="w-10 h-10 text-gray-300 mb-4" />
              <p className="font-medium text-gray-800 mb-1">
                {selectedCollection
                  ? "Cliquez pour sélectionner votre ZIP"
                  : "Sélectionnez d'abord une collection"}
              </p>
              <p className="text-xs text-gray-400">Fichier .zip uniquement</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                className="hidden"
                onChange={handleFileSelect}
                disabled={!selectedCollection}
              />
            </div>
          </div>
        )}

        {/* ── PARSING ── */}
        {step === "parsing" && (
          <div className="flex items-center gap-3 p-6 border border-gray-200 rounded-xl bg-gray-50">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            <span className="text-sm text-gray-600">Analyse de la structure du ZIP…</span>
          </div>
        )}

        {/* ── PREVIEW ── */}
        {step === "preview" && (
          <div className="space-y-6">
            {/* Résumé */}
            <div className="p-5 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">
                  {artists.length} artiste{artists.length > 1 ? "s" : ""} —{" "}
                  {totalArtworks} fiche{totalArtworks > 1 ? "s" : ""} à créer —{" "}
                  {totalFiles} fichier{totalFiles > 1 ? "s" : ""}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Collection : {collections.find(c => c.id === selectedCollection)?.title}
                </p>
              </div>
              <button type="button" onClick={handleReset}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-700" />
              </button>
            </div>

            {/* Détail par artiste */}
            <div className="space-y-2">
              {artists.map((artist) => (
                <div key={artist.name} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition text-left"
                    onClick={() => setExpandedArtist(
                      expandedArtist === artist.name ? null : artist.name
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <FolderOpen className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900 text-sm">{artist.name}</span>
                      <span className="text-xs text-gray-400">
                        {artist.artworks.length} œuvre{artist.artworks.length > 1 ? "s" : ""}
                        {artist.cvFiles.length > 0 && ` · ${artist.cvFiles.length} CV`}
                      </span>
                    </div>
                    {expandedArtist === artist.name
                      ? <ChevronUp className="w-4 h-4 text-gray-400" />
                      : <ChevronDown className="w-4 h-4 text-gray-400" />
                    }
                  </button>

                  {expandedArtist === artist.name && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                      {artist.artworks.map((aw) => (
                        <div key={aw.inventoryNumber} className="px-4 py-3 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-mono text-gray-700">{aw.inventoryNumber}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            {aw.images.length > 0 && (
                              <span className="flex items-center gap-1">
                                <ImageIcon className="w-3.5 h-3.5" />
                                {aw.images.length}
                              </span>
                            )}
                            {aw.docs.length > 0 && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3.5 h-3.5" />
                                {aw.docs.length}
                              </span>
                            )}
                            {aw.images.length === 0 && (
                              <span className="text-amber-500">⚠ photo manquante</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Toggle mode enrichissement */}
            <div style={{
              padding: "14px 16px",
              background: enrichMode ? "#fdf9f0" : "#f9fafb",
              border: `1px solid ${enrichMode ? "#c9a961" : "#e5e7eb"}`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}>
              <input
                type="checkbox"
                id="enrichMode"
                checked={enrichMode}
                onChange={e => setEnrichMode(e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#c9a961" }}
              />
              <label htmlFor="enrichMode" style={{ cursor: "pointer", flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
                  Enrichir les fiches existantes
                </div>
                <div style={{ fontSize: 12, color: "#6b6b6b", marginTop: 2 }}>
                  Cherche par n° inventaire et ajoute photos + documents aux fiches déjà créées. Crée une nouvelle fiche si non trouvée.
                </div>
              </label>
            </div>

            {/* Boutons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleConfirmImport}
                className="px-6 py-2.5 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition"
              >
                {enrichMode
                  ? `Enrichir ${totalArtworks} fiche${totalArtworks > 1 ? "s" : ""}`
                  : `Créer ${totalArtworks} fiche${totalArtworks > 1 ? "s" : ""}`
                }
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* ── IMPORT EN COURS ── */}
        {step === "importing" && (
          <div className="border border-gray-200 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">Import en cours…</p>
                <p className="text-xs text-gray-500 truncate">{progress.label}</p>
              </div>
              <span className="text-sm text-gray-500 flex-shrink-0">
                {progress.current} / {progress.total}
              </span>
            </div>

            {/* Barre de progression */}
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-black h-2 rounded-full transition-all duration-300"
                style={{
                  width: progress.total > 0
                    ? `${Math.round((progress.current / progress.total) * 100)}%`
                    : "0%"
                }}
              />
            </div>

            <p className="text-xs text-gray-400 text-center">
              Ne fermez pas cette page pendant l'import
            </p>
          </div>
        )}

        {/* ── DONE ── */}
        {step === "done" && (
          <div className="space-y-4">
            <div className="p-6 border border-emerald-200 rounded-xl bg-emerald-50">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-900">
                    {progress.current - progress.errors.length} fiche
                    {progress.current - progress.errors.length > 1 ? "s" : ""} créées avec succès
                  </p>
                  {progress.errors.length > 0 && (
                    <p className="text-sm text-emerald-700 mt-1">
                      {progress.errors.length} fiche{progress.errors.length > 1 ? "s" : ""} en erreur (voir détails ci-dessous)
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Erreurs détaillées */}
            {progress.errors.length > 0 && (
              <div className="p-4 border border-red-200 rounded-xl bg-red-50">
                <p className="text-sm font-medium text-red-800 mb-2">Erreurs :</p>
                <ul className="space-y-1">
                  {progress.errors.map((e, i) => (
                    <li key={i} className="text-xs text-red-700 font-mono">{e}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-2.5 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 transition"
              >
                Voir ma collection
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
              >
                Importer un autre dossier
              </button>
            </div>
          </div>
        )}

        {/* ── ERREUR ── */}
        {step === "error" && (
          <div className="flex items-start gap-3 p-5 border border-red-200 rounded-xl bg-red-50">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{errorMsg}</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-3 text-sm text-red-700 underline"
              >
                Réessayer
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}